import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { signInWithPopup } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { auth, googleProvider } from "./firebase-config/firebase";
import { url } from "../ConstUrl/Url";
import { colors, fonts, size } from "../ColorFont/color";
import { useUser } from "../context/UserContext";
import BaseButton from "../components/common/BaseButton";
import RoleCard from "./components/RoleCard";
import { IconShoppingBag, IconSeeding, IconArrowLeft, IconBrandGoogleFilled, IconSparkles } from "@tabler/icons-react";

// Inner components for Role Icons to keep RoleCard clean
const BuyerIcon = ({ color }) => (
  <IconShoppingBag color={color} size={28} stroke={1.5} />
);

const FarmerIcon = ({ color }) => (
  <IconSeeding color={color} size={28} stroke={1.5} />
);

const SignUp = ({ onClose, onRoleSelectionChange }) => {
  const { t } = useTranslation();
  const { refreshUserData } = useUser();

  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (onRoleSelectionChange) {
      onRoleSelectionChange(showRoleSelection);
    }
  }, [showRoleSelection, onRoleSelectionChange]);

  const signInWithGoogle = async () => {
    setLoadingGoogle(true);
    try {
      let user = auth.currentUser;
      if (!user) {
        const result = await signInWithPopup(auth, googleProvider);
        user = result.user;
      }

      // Step 1: Check if user exists in the system
      const response = await fetch(`${url}/api/check-user/${user.email}`);
      const data = await response.json();

      if (data.exists) {
        // Step 2: User exists — fetch their full profile (includes role)
        await refreshUserData(user.email);

        // Step 3: Close dialog FIRST, then navigate
        if (onClose) onClose();
        navigate("/home");
      } else {
        // New user
        if (selectedRole) {
          // If role is already selected, go to registration form
          if (onClose) onClose();
          navigate(`/userform?role=${selectedRole}`);
        } else {
          // If no role selected, show role selection screen
          setShowRoleSelection(true);
        }
      }
    } catch (error) {
      console.error("Google Login Error:", error);
      alert(`Error logging in with Google: ${error.message || "Please try again."}`);
    } finally {
      setLoadingGoogle(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "transparent",
        fontFamily: fonts.poppins,
      }}
    >
      <Box sx={{ width: "100%", zIndex: 2 }}>
        {!showRoleSelection ? (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "transparent",
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: size.h2,
                lineHeight: 1.4,
                letterSpacing: "-0.03rem",
                color: colors.text_dark,
                mb: '0.5rem',
                fontFamily: fonts.poppins,
                textAlign: 'center',
              }}
            >
              {t("t&cWelcome")}
            </Typography>

            <Typography
              sx={{
                fontWeight: 400,
                fontSize: size.body,
                lineHeight: 1.5,
                color: colors.text,
                // 1.5rem matches BaseButton primary mb — equal visual weight before/after
                mb: '1.5rem',
                fontFamily: fonts.poppins,
                textAlign: 'center',
              }}
            >
              {t("loginOptions")}
            </Typography>

            <BaseButton
              onClick={() => setShowRoleSelection(true)}
              icon={<IconSparkles size={20} />}
            >
              {t("yes_new")}
            </BaseButton>

            <BaseButton
              variant="outlined"
              onClick={signInWithGoogle}
              loading={loadingGoogle}
              icon={<IconBrandGoogleFilled size={20} color="#EA4335" />}
              sx={{ mb: 0 }}
            >
              {t("no_google")}
            </BaseButton>
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              backgroundColor: "transparent",
            }}
          >
            {/* Back Button */}
            <Box
              onClick={() => setShowRoleSelection(false)}
              sx={{
                position: "absolute",
                top: { xs: "-10px", sm: "-20px" },
                left: 0,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                padding: "8px 0",
                "&:hover span": { textDecoration: "underline" },
              }}
            >
              <IconArrowLeft
                color={colors.text}
                size={20}
                stroke={1.5}
                style={{ marginTop: "4px" }}
              />
              {/* Back Text matching Figma specs */}
              <Typography
                component="span"
                sx={{
                  fontFamily: fonts.poppins,
                  fontSize: size.body, // 16px is close to height 22
                  fontWeight: 400,
                  color: colors.text,
                  width: "35.03px",
                  height: "22px",
                  opacity: 1,
                  transform: "rotate(0deg)",
                  mt: "1px", // top: 1px relative to container
                  ml: "0px", // we use gap: 8px to handle left: 24px (16px img + 8px gap = 24px left)
                  lineHeight: "22px"
                }}
              >
                {t("back")}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontFamily: fonts.poppins,
                fontSize: size.h1_role,
                fontWeight: 700,
                lineHeight: 1.3,
                textAlign: "center",
                letterSpacing: "-0.0375rem",
                color: colors.text_dark,
                mt: { xs: 'clamp(1.5rem, 4vh, 2.5rem)', sm: 'clamp(1rem, 3vh, 1.5rem)' }, // vh compressed margins
                mb: 'clamp(0.25rem, 1vh, 0.5rem)', // compress tiny native spacing 
              }}
            >
              {t("selectRole")}
            </Typography>

            <Typography
              sx={{
                fontFamily: fonts.poppins,
                fontSize: size.body,
                fontWeight: 400,
                lineHeight: 1.5,
                textAlign: "center",
                color: colors.text,
                mb: 'clamp(1rem, 3vh, 2rem)', // mapped vh to fluid margin bounds for small laptops
              }}
            >
              {t("selectRoleSub")}
            </Typography>

            <RoleCard
              title={t("buyer")}
              description={t("buyCropDesc")}
              tags={[t("tagBrowse"), t("tagContact"), t("tagCompare"), t("tagSave")]}
              icon={BuyerIcon}
              selected={selectedRole === "Buyer"}
              onClick={() => setSelectedRole("Buyer")}
            />

            <RoleCard
              title={t("farmer")}
              description={t("sellCropDesc")}
              tags={[t("tagList"), t("tagEarn"), t("tagVerify"), t("tagWithdraw")]}
              icon={FarmerIcon}
              selected={selectedRole === "Seller"}
              onClick={() => setSelectedRole("Seller")}
            />

            <BaseButton
              onClick={signInWithGoogle}
              loading={loadingGoogle}
              disabled={!selectedRole}
              sx={{
                backgroundColor: colors.primary_continue,
                boxShadow: colors.shadow_continue,
                mt: 'clamp(0.5rem, 2vh, 1rem)' // squish vertically on small devices
              }}
            >
              {t("continue")}
            </BaseButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SignUp;